import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://compliance-backend.cloudsanalytics.ai';

class AttackApiService {
  constructor() {
    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for Cognito token authentication
    this.apiClient.interceptors.request.use(
      (config) => {
        // Add Cognito access token if available
        const accessToken = localStorage.getItem('cognito_token');
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        } else {
          // If no token, reject the request
          return Promise.reject(new Error('No authentication token available'));
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('Attack API Error:', error);
        if (error.response?.status === 401) {
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Fetch attack alerts for a specific agent
   * @param {string} agentId - The ID of the agent
   * @returns {Promise<Object>} Attack alerts data
   */
  async getAttackAlerts(agentId) {
    try {
      const response = await this.apiClient.get(`/attack/${agentId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching attack alerts for agent ${agentId}:`, error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch attack alerts'
      );
    }
  }

  /**
   * Get attack statistics for a specific agent
   * @param {string} agentId - The ID of the agent
   * @returns {Promise<Object>} Attack statistics
   */
  async getAttackStatistics(agentId) {
    try {
      const response = await this.apiClient.get(`/attack/${agentId}/stats`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching attack statistics for agent ${agentId}:`, error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch attack statistics'
      );
    }
  }

  /**
   * Get recent attack alerts for a specific agent
   * @param {string} agentId - The ID of the agent
   * @param {number} limit - Number of recent alerts to fetch (default: 10)
   * @returns {Promise<Object>} Recent attack alerts
   */
  async getRecentAttackAlerts(agentId, limit = 10) {
    try {
      const response = await this.apiClient.get(`/attack/${agentId}/recent`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching recent attack alerts for agent ${agentId}:`, error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch recent attack alerts'
      );
    }
  }

  /**
   * Categorize attack severity based on rule level
   * @param {number} level - Rule level from the alert
   * @returns {string} Severity category
   */
  categorizeAttackSeverity(level) {
    if (level >= 10) return 'Critical';
    if (level >= 7) return 'High';
    if (level >= 4) return 'Medium';
    if (level >= 1) return 'Low';
    return 'Info';
  }

  /**
   * Get severity color for UI display
   * @param {string} severity - Severity level
   * @returns {string} Material-UI color
   */
  getSeverityColor(severity) {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  }

  /**
   * Process attack alerts data for UI display
   * @param {Array} alerts - Raw alerts data
   * @returns {Object} Processed data with statistics and categorized alerts
   */
  processAttackData(alerts) {
    if (!alerts || !Array.isArray(alerts)) {
      return {
        totalAlerts: 0,
        severityStats: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
        recentAlerts: [],
        topAttackTypes: [],
        sourceIPs: []
      };
    }

    const severityStats = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    const attackTypes = {};
    const sourceIPs = {};

    alerts.forEach(alert => {
      // Categorize by severity
      const severity = this.categorizeAttackSeverity(alert.rule?.level || 0).toLowerCase();
      severityStats[severity] = (severityStats[severity] || 0) + 1;

      // Count attack types
      const attackType = alert.rule?.description || 'Unknown';
      attackTypes[attackType] = (attackTypes[attackType] || 0) + 1;

      // Count source IPs
      const sourceIP = alert.data?.srcip || alert.srcip || 'Unknown';
      sourceIPs[sourceIP] = (sourceIPs[sourceIP] || 0) + 1;
    });

    // Get top attack types
    const topAttackTypes = Object.entries(attackTypes)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Get top source IPs
    const topSourceIPs = Object.entries(sourceIPs)
      .map(([ip, count]) => ({ ip, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalAlerts: alerts.length,
      severityStats,
      recentAlerts: alerts.slice(0, 10), // Most recent 10 alerts
      topAttackTypes,
      sourceIPs: topSourceIPs
    };
  }
}

const attackApiService = new AttackApiService();

export default attackApiService;
